import { useCallback, useEffect, useState } from 'react'
import {
  apiListEvents,
  apiMarkAllEventsRead,
  apiMarkEventRead,
} from './studioApi.js'
import { Badge } from './ui-kit/Badge.jsx'
import { Button } from './ui-kit/Button.jsx'
import { Drawer } from './ui-kit/Drawer.jsx'
import { EmptyState } from './ui-kit/EmptyState.jsx'
import { BellIcon } from './ui-kit/Icon.jsx'
import { fmtDateTime } from './studioConstants.js'

// Wave 38 — notification center / activity feed.
// Wave 47x.2 — full migration to studio CSS classes + Badge primitive.
// Was 13 inline-styled blocks + a hardcoded SEVERITY_BADGE_COLORS map
// using off-palette hex values (#a7b1c2 / #d7a264 / #de7b72) that
// didn't match the unified success/warning/error tokens. Now every
// surface uses the studio class system or the Badge primitive.
//
// Bell icon in the topbar polls /api/events?unreadOnly=true&limit=1
// every 30s for the unread count badge. Clicking opens a side drawer
// with the last 50 events. Filter chips scope to autonomy / voice /
// brand-safety / engagement categories.

const FILTER_CHIPS = [
  { id: 'all', label: 'All', kinds: null },
  {
    id: 'autonomy',
    label: 'Autonomy',
    kinds: [
      'auto-publish-success',
      'auto-publish-failure',
      'kill-switch-engaged',
      'kill-switch-cleared',
      'cadence-skipped',
    ],
  },
  {
    id: 'voice',
    label: 'Voice',
    kinds: ['voice-drift', 'voice-corpus-proposal'],
  },
  {
    id: 'brand-safety',
    label: 'Brand safety',
    kinds: ['brand-safety-block', 'brand-safety-override-approved'],
  },
  {
    id: 'topics',
    label: 'Topics',
    kinds: ['auto-discover-topic', 'auto-promote-topic'],
  },
  {
    id: 'incidents',
    label: 'Incidents',
    kinds: ['incident-opened', 'incident-resolved', 'pillar-flagged-deprecate'],
  },
]

// Severity → Badge tone. Uses the platform's existing tone tokens
// instead of hardcoded hex. info/warning/critical map to the studio
// tone scale; everything else lands on muted.
function severityTone(severity) {
  if (severity === 'critical') return 'error'
  if (severity === 'warning') return 'warning'
  if (severity === 'info') return 'muted'
  return 'muted'
}

const POLL_INTERVAL_MS = 30_000

export function NotificationBell() {
  const [unreadCount, setUnreadCount] = useState(0)
  const [open, setOpen] = useState(false)
  const [events, setEvents] = useState([])
  const [loading, setLoading] = useState(false)
  const [activeFilter, setActiveFilter] = useState('all')
  const [error, setError] = useState('')

  const refreshCount = useCallback(async () => {
    try {
      const { unreadCount: count } = await apiListEvents({
        unreadOnly: true,
        limit: 1,
      })
      setUnreadCount(Number(count || 0))
    } catch {
      // Bell is best-effort; a fetch failure shouldn't disrupt the UI.
    }
  }, [])

  const loadDrawer = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const filter = FILTER_CHIPS.find((c) => c.id === activeFilter)
      const { events: list, unreadCount: count } = await apiListEvents({
        kinds: filter?.kinds || null,
        limit: 50,
      })
      setEvents(list || [])
      setUnreadCount(Number(count || 0))
    } catch (err) {
      setError(err?.message || 'Failed to load events')
    } finally {
      setLoading(false)
    }
  }, [activeFilter])

  useEffect(() => {
    refreshCount()
    const id = setInterval(refreshCount, POLL_INTERVAL_MS)
    const onVisibility = () => {
      if (document.visibilityState === 'visible') refreshCount()
    }
    document.addEventListener('visibilitychange', onVisibility)
    return () => {
      clearInterval(id)
      document.removeEventListener('visibilitychange', onVisibility)
    }
  }, [refreshCount])

  useEffect(() => {
    if (open) loadDrawer()
  }, [open, loadDrawer])

  const handleMarkRead = async (id) => {
    try {
      const { unreadCount: count } = await apiMarkEventRead(id)
      setUnreadCount(Number(count || 0))
      setEvents((prev) =>
        prev.map((event) =>
          event.id === id
            ? { ...event, readAt: new Date().toISOString() }
            : event
        )
      )
    } catch (err) {
      setError(err?.message || 'Failed to mark read')
    }
  }

  const handleMarkAllRead = async () => {
    try {
      await apiMarkAllEventsRead()
      setUnreadCount(0)
      setEvents((prev) =>
        prev.map((event) => ({
          ...event,
          readAt: event.readAt || new Date().toISOString(),
        }))
      )
    } catch (err) {
      setError(err?.message || 'Failed to mark all read')
    }
  }

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label={
          unreadCount > 0
            ? `${unreadCount} unread notifications`
            : 'Notifications'
        }
        data-testid="notification-bell"
        className={`studio-notification-bell${unreadCount > 0 ? ' has-unread' : ''}`}
      >
        <BellIcon size="lg" />
        {unreadCount > 0 && (
          <span
            data-testid="notification-unread-count"
            className="studio-notification-bell-badge"
          >
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>
      <Drawer open={open} onClose={() => setOpen(false)} title="Activity">
        <div data-testid="notification-drawer" className="studio-activity">
          <div className="studio-activity-status">
            <span>
              {unreadCount === 0
                ? 'Activity is quiet.'
                : `${unreadCount} unread event${unreadCount === 1 ? '' : 's'}`}
            </span>
            {unreadCount > 0 ? (
              <Button variant="ghost" size="sm" onClick={handleMarkAllRead}>
                Mark all read
              </Button>
            ) : null}
          </div>
          <div
            role="tablist"
            aria-label="Activity category"
            className="studio-activity-tabs"
          >
            {FILTER_CHIPS.map((chip) => {
              const active = activeFilter === chip.id
              return (
                <button
                  key={chip.id}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  onClick={() => setActiveFilter(chip.id)}
                  className={`studio-activity-tab ${active ? 'active' : ''}`}
                >
                  {chip.label}
                </button>
              )
            })}
          </div>

          {error && (
            <div role="alert" className="studio-activity-error">
              {error}
            </div>
          )}

          {loading && events.length === 0 && (
            <div className="studio-activity-loading">Loading activity…</div>
          )}

          {!loading && events.length === 0 && !error && (
            <EmptyState
              illustration="heartbeat"
              title="Nothing to attend to."
              body={
                activeFilter === 'all'
                  ? 'Autonomy decisions, voice drift, brand-safety hits, and incidents land here as they happen.'
                  : 'Switch the filter or wait — events surface here as the autonomy loop runs.'
              }
            />
          )}

          <div className="studio-activity-list">
            {events.map((event) => {
              const isUnread = !event.readAt
              return (
                <div
                  key={event.id}
                  data-testid="notification-event"
                  className={`studio-activity-event ${isUnread ? 'unread' : ''}`}
                  onClick={() => isUnread && handleMarkRead(event.id)}
                >
                  <div className="studio-activity-event-head">
                    <Badge tone={severityTone(event.severity)}>
                      {event.kind.replace(/-/g, ' ')}
                    </Badge>
                    <span className="studio-activity-event-time">
                      {fmtDateTime(event.createdAt)}
                    </span>
                  </div>
                  <div className="studio-activity-event-summary">
                    {event.summary}
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      </Drawer>
    </>
  )
}
